"use client";

import { useEffect, useState } from "react";
import { RefreshCw, AlertTriangle, CheckCircle, Info, Calendar, ListTodo, Clock, Sparkles } from "lucide-react";

const API = process.env.NEXT_PUBLIC_API_URL || "http://100.68.12.86:3200";

// ── Tipos ────────────────────────────────────────────────────────────────────

interface AgendaItem {
  hora: string;
  titulo: string;
}

interface PlanItem {
  titulo: string;
  motivo: string;
  duracionMin?: number;
  duracionAprox?: boolean;
  origen?: string;
  taskId?: string;
}

interface FueraItem {
  titulo: string;
  motivo: string;
}

interface BriefPayload {
  saludo?: string;
  resumen?: string;
  agenda?: AgendaItem[];
  plan?: PlanItem[];
  fuera?: FueraItem[];
  avisos?: string[];
}

interface BriefData {
  status: "ok" | "none" | "error";
  createdAt?: string;
  payload?: BriefPayload;
  // El backend hace spread del payload en el raíz — estos campos pueden llegar directamente
  saludo?: string;
  resumen?: string;
  agenda?: AgendaItem[];
  plan?: PlanItem[];
  fuera?: FueraItem[];
  avisos?: string[];
}

// ── Lógica de gravedad de avisos ─────────────────────────────────────────────

type Gravedad = "red" | "amber" | "info";

function inferGravedad(texto: string): Gravedad {
  const t = texto.toLowerCase();
  if (t.includes("vencida") || t.includes("🔴") || t.includes("vence hoy") || t.includes("urgente")) return "red";
  if (t.includes("vence") || t.includes("pendiente") || t.includes("sin responder") || t.includes("⚠️")) return "amber";
  return "info";
}

const GRAVEDAD_ORDER: Record<Gravedad, number> = { red: 0, amber: 1, info: 2 };

// ── Componentes ──────────────────────────────────────────────────────────────

function AvisoRow({ texto }: { texto: string }) {
  const g = inferGravedad(texto);
  const cfg = {
    red:   { color: "var(--bad)",  bg: "rgba(255,107,107,.08)", borderLeft: "3px solid var(--bad)",  icon: <AlertTriangle size={13} /> },
    amber: { color: "var(--warn)", bg: "rgba(246,178,74,.08)",  borderLeft: "3px solid var(--warn)", icon: <AlertTriangle size={13} /> },
    info:  { color: "var(--ok)",   bg: "transparent",           borderLeft: "3px solid transparent", icon: <CheckCircle   size={13} /> },
  }[g];

  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: "8px",
      padding: "8px 10px",
      background: cfg.bg,
      borderLeft: cfg.borderLeft,
      borderRadius: g === "info" ? "0" : "0 6px 6px 0",
      marginBottom: "4px",
    }}>
      <span style={{ color: cfg.color, flexShrink: 0, marginTop: "1px" }}>{cfg.icon}</span>
      <span style={{ fontSize: "13px", color: g === "info" ? "var(--tx2)" : "var(--tx)", lineHeight: 1.4 }}>{texto}</span>
    </div>
  );
}

function PlanRow({ item, idx }: { item: PlanItem; idx: number }) {
  const [checked, setChecked] = useState(false);
  const origen = item.origen?.toLowerCase() ?? "";
  const dotColor =
    origen.includes("urgent") ? "var(--bad)" :
    origen.includes("high")   ? "var(--warn)" :
    origen.includes("proj")   ? "var(--acc)" :
    "var(--tx3)";

  const durStr = item.duracionMin
    ? `${item.duracionAprox ? "~" : ""}${item.duracionMin < 60 ? `${item.duracionMin}min` : `${Math.round(item.duracionMin / 60)}h`}`
    : null;

  return (
    <div style={{
      display: "flex", gap: "10px", padding: "10px 0",
      borderBottom: "1px solid var(--line2)",
      opacity: checked ? 0.45 : 1,
      transition: "opacity 180ms",
    }}>
      {/* Checkbox */}
      <div
        onClick={() => setChecked(v => !v)}
        style={{
          flexShrink: 0, marginTop: "2px",
          width: "16px", height: "16px", borderRadius: "4px",
          border: `2px solid ${checked ? "var(--ok)" : "var(--line)"}`,
          background: checked ? "var(--ok)" : "transparent",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 150ms",
        }}
      >
        {checked && <span style={{ color: "#000", fontSize: "10px", fontWeight: 700, lineHeight: 1 }}>✓</span>}
      </div>

      {/* Contenido */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <span style={{
            fontSize: "13px", fontWeight: 600, color: "var(--tx)",
            textDecoration: checked ? "line-through" : "none",
          }}>
            {item.titulo}
          </span>
          {durStr && (
            <span style={{
              fontSize: "10px", padding: "1px 7px", borderRadius: "10px",
              background: "var(--panel2)", color: "var(--tx2)",
              border: "1px solid var(--line)", flexShrink: 0,
            }}>
              {durStr}
            </span>
          )}
          <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
        </div>
        {item.motivo && (
          <p style={{
            fontSize: "12px", color: "var(--tx2)", margin: "3px 0 0",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {item.motivo}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Card wrapper ─────────────────────────────────────────────────────────────

function Card({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{
      background: "var(--panel)",
      border: "1px solid var(--line)",
      borderRadius: "12px",
      padding: "20px",
      display: "flex", flexDirection: "column", gap: "4px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "7px", marginBottom: "12px" }}>
        <span style={{ color: "var(--tx3)" }}>{icon}</span>
        <h2 style={{ fontSize: "13px", fontWeight: 700, color: "var(--tx2)", textTransform: "uppercase", letterSpacing: "0.06em", margin: 0 }}>{title}</h2>
      </div>
      {children}
    </div>
  );
}

// ── Estado vacío ─────────────────────────────────────────────────────────────

function EstadoVacio({ onRefresh, onGenerate, generating }: {
  onRefresh: () => void;
  onGenerate: () => void;
  generating: boolean;
}) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: "80px 20px", gap: "16px", textAlign: "center",
    }}>
      <Sparkles size={40} style={{ color: generating ? "var(--acc)" : "var(--tx3)", opacity: generating ? 1 : 0.5,
        transition: "color 300ms, opacity 300ms" }} />
      <p style={{ fontSize: "16px", fontWeight: 600, color: "var(--tx)", margin: 0 }}>
        {generating ? "Mia está generando el brief…" : "Mia aún no ha generado el brief de hoy"}
      </p>
      <p style={{ fontSize: "13px", color: "var(--tx2)", margin: 0, maxWidth: "380px" }}>
        {generating
          ? "Esto tarda un momento. La página se actualizará sola cuando esté listo."
          : "El brief se genera automáticamente a las 07:00 de lunes a viernes. Para generarlo ahora, pulsa el botón."}
      </p>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
        {!generating && (
          <button
            onClick={onGenerate}
            style={{
              display: "flex", alignItems: "center", gap: "7px",
              padding: "9px 22px",
              background: "var(--acc)", border: "none",
              borderRadius: "8px", color: "#fff", fontSize: "13px",
              fontWeight: 600, cursor: "pointer",
            }}
          >
            <Sparkles size={13} /> Generar brief ahora
          </button>
        )}
        <button
          onClick={onRefresh}
          disabled={generating}
          style={{
            display: "flex", alignItems: "center", gap: "7px",
            padding: "9px 20px",
            background: "var(--panel2)", border: "1px solid var(--line)",
            borderRadius: "8px", color: "var(--tx)", fontSize: "13px",
            fontWeight: 500, cursor: generating ? "default" : "pointer",
            opacity: generating ? 0.5 : 1,
          }}
        >
          <RefreshCw size={13} className={generating ? "animate-spin" : ""} />
          {generating ? "Esperando…" : "Refrescar"}
        </button>
      </div>
    </div>
  );
}

// ── Planificador ─────────────────────────────────────────────────────────────

function Planificador() {
  const [horas, setHoras] = useState("");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <p style={{ fontSize: "12px", color: "var(--tx2)", margin: 0, lineHeight: 1.5 }}>
        Dile a Mia cuánto tiempo tienes y ella te armará un plan priorizado.
      </p>
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <span style={{ fontSize: "13px", color: "var(--tx2)", flexShrink: 0 }}>Tengo</span>
        <input
          type="number" min="1" max="12" placeholder="2"
          value={horas}
          onChange={e => setHoras(e.target.value)}
          style={{
            width: "60px", padding: "6px 10px",
            background: "var(--panel2)", border: "1px solid var(--line)",
            borderRadius: "7px", color: "var(--tx)", fontSize: "13px",
            outline: "none", textAlign: "center",
          }}
        />
        <span style={{ fontSize: "13px", color: "var(--tx2)", flexShrink: 0 }}>horas libres</span>
      </div>
      <p style={{ fontSize: "11px", color: "var(--tx3)", margin: 0, lineHeight: 1.5 }}>
        Escríbelo en Telegram: <span style={{ color: "var(--tx2)", fontStyle: "italic" }}>"Mia, tengo {horas || "X"} horas. ¿Qué hago primero?"</span>
      </p>
    </div>
  );
}

// ── Página principal ─────────────────────────────────────────────────────────

export default function MiaPage() {
  const [brief, setBrief]         = useState<BriefData | null>(null);
  const [loading, setLoading]     = useState(true);
  const [showAll, setShowAll]     = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = (quiet = false) => {
    if (!quiet) setLoading(true);
    fetch(`${API}/mia/brief`)
      .then(r => r.json())
      .then((d: BriefData) => {
        setBrief(d);
        if (!quiet) setLoading(false);
        if (generating && d.status === "ok") {
          setGenerating(false);
          setLoading(false);
        }
      })
      .catch(() => { setBrief({ status: "none" }); if (!quiet) setLoading(false); });
  };

  // Polling mientras Mia genera el brief
  useEffect(() => {
    if (!generating) return;
    const interval = setInterval(() => load(true), 8000);
    return () => clearInterval(interval);
  }, [generating]);

  const generateBrief = () => {
    setGenerating(true);
    fetch(`${API}/mia/generar-brief`, { method: "POST" })
      .then(r => r.json())
      .catch(() => null); // el hook es fire-and-forget; el polling detecta cuando acaba
  };

  useEffect(() => { load(); }, []);

  // ── Fecha formateada ────────────────────────────────────────────────────────
  const fechaHoy = new Date().toLocaleDateString("es-ES", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  const generadoA = brief?.createdAt
    ? new Date(brief.createdAt).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })
    : null;

  // El backend devuelve los campos del payload spreadeados en el raíz (sin anidar en .payload)
  const payload: BriefPayload = brief?.payload ?? {
    saludo:  brief?.saludo,
    resumen: brief?.resumen,
    agenda:  brief?.agenda,
    plan:    brief?.plan,
    fuera:   brief?.fuera,
    avisos:  brief?.avisos,
  };

  // Avisos ordenados por gravedad
  const avisosOrdenados = [...(payload.avisos ?? [])].sort(
    (a, b) => GRAVEDAD_ORDER[inferGravedad(a)] - GRAVEDAD_ORDER[inferGravedad(b)]
  );
  const avisosVis = showAll ? avisosOrdenados : avisosOrdenados.slice(0, 5);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="animate-fade-in" style={{ maxWidth: "1180px", margin: "0 auto", padding: "0 0 40px" }}>

      {/* ── Toolbar ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div>
          <h1 style={{ fontSize: "22px", fontWeight: 700, color: "var(--tx)", margin: 0, display: "flex", alignItems: "center", gap: "9px" }}>
            <Sparkles size={20} style={{ color: "var(--acc)" }} />
            Daily Brief
          </h1>
          <p style={{ fontSize: "12px", color: "var(--tx3)", margin: "3px 0 0", textTransform: "capitalize" }}>{fechaHoy}</p>
        </div>
        <button
          onClick={() => load()}
          style={{
            display: "flex", alignItems: "center", gap: "6px",
            padding: "8px 14px", background: "var(--panel)", border: "1px solid var(--line)",
            borderRadius: "8px", fontSize: "12px", color: "var(--tx2)", cursor: "pointer",
          }}
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          {loading ? "Cargando..." : "Actualizar"}
        </button>
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px", color: "var(--tx3)" }}>
          <RefreshCw size={18} className="animate-spin" />
        </div>
      )}

      {/* ── Sin brief ── */}
      {!loading && brief?.status === "none" && <EstadoVacio onRefresh={load} onGenerate={generateBrief} generating={generating} />}

      {/* ── Brief disponible ── */}
      {!loading && brief?.status === "ok" && (
        <>
          {/* Banner hero */}
          <div style={{
            background: "linear-gradient(180deg, var(--panel2), var(--panel))",
            border: "1px solid var(--line)",
            borderRadius: "12px",
            padding: "28px 32px",
            marginBottom: "14px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 style={{ fontSize: "26px", fontWeight: 700, color: "var(--tx)", margin: "0 0 6px", lineHeight: 1.2 }}>
                  {payload.saludo ?? "Buenos días, Didac."}
                </h2>
                {payload.resumen && (
                  <p style={{ fontSize: "14px", color: "var(--tx2)", margin: 0, lineHeight: 1.6, maxWidth: "720px" }}>
                    {payload.resumen}
                  </p>
                )}
              </div>
              {generadoA && (
                <div style={{ flexShrink: 0, textAlign: "right" }}>
                  <span style={{ fontSize: "11px", color: "var(--tx3)" }}>Generado a las</span>
                  <p style={{ fontSize: "18px", fontWeight: 700, color: "var(--tx2)", margin: "2px 0 0" }}>{generadoA}</p>
                </div>
              )}
            </div>
          </div>

          {/* Grid 2 columnas */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr",
            gap: "14px",
          }}
            className="brief-grid"
          >
            {/* ── Columna izquierda ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>

              {/* Qué hacer hoy */}
              <Card title="Qué hacer hoy" icon={<ListTodo size={14} />}>
                {(payload.plan ?? []).length === 0 ? (
                  <p style={{ fontSize: "13px", color: "var(--tx3)", margin: 0 }}>Sin tareas planificadas para hoy.</p>
                ) : (
                  (payload.plan ?? []).map((item, i) => <PlanRow key={i} item={item} idx={i} />)
                )}
                {(payload.fuera ?? []).length > 0 && (
                  <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid var(--line2)" }}>
                    <p style={{ fontSize: "11px", fontWeight: 600, color: "var(--tx3)", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 8px" }}>
                      Fuera del plan
                    </p>
                    {payload.fuera!.map((f, i) => (
                      <div key={i} style={{ fontSize: "12px", color: "var(--tx3)", padding: "4px 0", display: "flex", gap: "6px" }}>
                        <span style={{ color: "var(--tx3)" }}>—</span>
                        <span>{f.titulo}{f.motivo ? <span style={{ color: "var(--tx3)" }}> · {f.motivo}</span> : null}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Planificador */}
              <Card title="Planificador" icon={<Clock size={14} />}>
                <Planificador />
              </Card>
            </div>

            {/* ── Columna derecha ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>

              {/* Avisos */}
              <Card title="Avisos" icon={<AlertTriangle size={14} />}>
                {avisosOrdenados.length === 0 ? (
                  <div style={{ display: "flex", gap: "7px", alignItems: "center" }}>
                    <CheckCircle size={14} style={{ color: "var(--ok)" }} />
                    <span style={{ fontSize: "13px", color: "var(--tx2)" }}>Sin avisos pendientes</span>
                  </div>
                ) : (
                  <>
                    {avisosVis.map((a, i) => <AvisoRow key={i} texto={a} />)}
                    {avisosOrdenados.length > 5 && (
                      <button
                        onClick={() => setShowAll(v => !v)}
                        style={{
                          background: "none", border: "none", cursor: "pointer",
                          fontSize: "12px", color: "var(--acc)", marginTop: "4px", textAlign: "left",
                          padding: 0,
                        }}
                      >
                        {showAll
                          ? "Ver menos"
                          : `Ver ${avisosOrdenados.length - 5} más`}
                      </button>
                    )}
                  </>
                )}
              </Card>

              {/* Agenda */}
              <Card title="Agenda de hoy" icon={<Calendar size={14} />}>
                {(payload.agenda ?? []).length === 0 ? (
                  <p style={{ fontSize: "13px", color: "var(--tx3)", margin: 0 }}>Sin eventos hoy.</p>
                ) : (
                  (payload.agenda ?? []).map((ev, i) => (
                    <div key={i} style={{
                      display: "flex", gap: "12px", alignItems: "flex-start",
                      padding: "7px 0", borderBottom: "1px solid var(--line2)",
                    }}>
                      <span style={{ fontSize: "12px", fontWeight: 700, color: "var(--acc)", flexShrink: 0, minWidth: "38px" }}>
                        {ev.hora}
                      </span>
                      <span style={{ fontSize: "13px", color: "var(--tx)" }}>{ev.titulo}</span>
                    </div>
                  ))
                )}
              </Card>
            </div>
          </div>
        </>
      )}

      {/* Responsive grid */}
      <style>{`
        @media (max-width: 900px) {
          .brief-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
